<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { ArrowRight, Languages, MessageSquare, SmilePlus, SpellCheck, Wand2 } from "lucide-vue-next";
  import { Spinner } from "@/components/ui/spinner";
  import AiStar from "./AiStar.vue";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Tooltip } from "@/components/ui/tooltip";
  import type { AiMenuSelection } from "./types";

  const COMMON_LANGUAGES = ["en", "fr", "es", "de", "it", "pt", "nl"];

  const props = withDefaults(
    defineProps<{
      variant?: "field" | "card";
      disabled?: boolean;
      busy?: boolean;
      empty?: boolean;
      showGenerate?: boolean;
      generateDisabled?: boolean;
      showEmoji?: boolean;
      languages?: string[];
      defaultLanguage?: string;
    }>(),
    {
      variant: "field",
      disabled: false,
      busy: false,
      empty: false,
      showGenerate: false,
      generateDisabled: false,
      showEmoji: false,
      languages: () => [],
      defaultLanguage: undefined,
    },
  );

  const emit = defineEmits<{
    select: [payload: AiMenuSelection];
  }>();

  const isOpen = defineModel<boolean>("open", { default: false });

  const customLang = ref("");
  const customPromptValue = ref("");

  watch(isOpen, (open) => {
    if (!open) {
      customLang.value = "";
      customPromptValue.value = "";
    }
  });

  const languageName = (code: string) => {
    try {
      const name = new Intl.DisplayNames([code], { type: "language" }).of(code) ?? code;
      return name.charAt(0).toUpperCase() + name.slice(1);
    } catch {
      return code;
    }
  };
  const surveyLanguages = computed(() => props.languages.filter(Boolean));
  const otherLanguages = computed(() => COMMON_LANGUAGES.filter((code) => !surveyLanguages.value.includes(code)));
  const defaultLanguageLabel = computed(() => (props.defaultLanguage ? languageName(props.defaultLanguage) : ""));

  const hasAnswersGroup = computed(() => props.showGenerate || props.showEmoji);

  const defaultAction = computed<AiMenuSelection | null>(() => {
    if (props.showGenerate) return props.generateDisabled ? null : { action: "generate", target: "answers" };
    return props.empty ? null : { action: "correct" };
  });
  const onEnter = (event: KeyboardEvent): void => {
    if (event.target !== event.currentTarget || !defaultAction.value) return;
    event.preventDefault();
    emit("select", defaultAction.value);
    isOpen.value = false;
  };

  const onTranslateCustom = (): void => {
    if (!customLang.value.trim()) return;
    emit("select", { action: "translate", lang: customLang.value.trim() });
    isOpen.value = false;
  };

  const onCustom = (): void => {
    if (!customPromptValue.value.trim()) return;
    emit("select", { action: "custom", prompt: customPromptValue.value.trim() });
    isOpen.value = false;
  };
</script>

<template>
  <Tooltip
    :content="busy ? 'Generating…' : 'AI actions'"
    :disabled="props.variant !== 'card'">
    <span class="inline-flex">
      <DropdownMenu v-model:open="isOpen">
        <DropdownMenuTrigger as-child>
          <Button
            type="button"
            variant="ghost"
            :size="props.variant === 'card' ? 'icon' : 'icon-sm'"
            class="text-primary active:scale-[0.96] disabled:opacity-50"
            :class="
              props.variant === 'field' &&
              'size-6 rounded-lg transition-colors duration-200 hover:bg-episto-teal/15 hover:text-episto-teal-700 active:bg-episto-teal/28 focus-visible:ring-[3px] focus-visible:ring-episto-teal/10'
            "
            :disabled="disabled"
            :aria-busy="busy"
            :aria-label="busy ? 'Generating…' : 'AI actions'"
            :title="props.variant === 'field' ? (busy ? 'Generating…' : 'AI') : undefined"
            @click.stop>
            <Spinner
              v-if="busy"
              class="size-3.5" />
            <AiStar
              v-else
              :class="props.variant === 'card' ? 'size-5' : 'size-[15px]'" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          class="w-80 p-0 [&_[role=menuitem]]:text-13"
          :class="props.variant === 'field' && 'w-72'"
          @keydown.enter="onEnter">
          <div class="p-1.5">
            <template v-if="hasAnswersGroup">
              <DropdownMenuLabel class="px-2 pb-1 pt-1.5 heading-label">Answers</DropdownMenuLabel>
              <DropdownMenuItem
                v-if="props.showGenerate"
                :disabled="props.generateDisabled"
                class="gap-2.5"
                @select="emit('select', { action: 'generate', target: 'answers' })">
                <AiStar class="size-4 text-muted-foreground" />
                Generate answers
                <kbd class="ml-auto rounded border bg-muted px-1.5 text-2xs text-muted-foreground">↵</kbd>
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="props.showEmoji"
                class="gap-2.5"
                @select="emit('select', { action: 'emoji' })">
                <SmilePlus class="size-4 text-muted-foreground" />
                Add emojis to all answers
              </DropdownMenuItem>
              <DropdownMenuSeparator class="my-1.5" />
              <DropdownMenuLabel class="px-2 pb-1 pt-1.5 heading-label">Whole card</DropdownMenuLabel>
            </template>

            <DropdownMenuItem
              :disabled="empty"
              class="gap-2.5"
              @select="emit('select', { action: 'correct' })">
              <SpellCheck class="size-4 text-muted-foreground" />
              Fix spelling
              <kbd
                v-if="!props.showGenerate"
                class="ml-auto rounded border bg-muted px-1.5 text-2xs text-muted-foreground"
                >↵</kbd
              >
            </DropdownMenuItem>
            <DropdownMenuItem
              :disabled="empty"
              class="gap-2.5"
              @select="emit('select', { action: 'rewrite' })">
              <Wand2 class="size-4 text-muted-foreground" />
              Rewrite
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger
                class="gap-2.5"
                :disabled="empty">
                <Languages class="size-4 text-muted-foreground" />
                Translate
                <span
                  v-if="defaultLanguageLabel"
                  class="ml-auto text-13 text-muted-foreground">
                  {{ defaultLanguageLabel }}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent class="w-60 p-1.5 [&_[role=menuitem]]:text-13">
                <template v-if="surveyLanguages.length">
                  <DropdownMenuLabel class="px-2 pb-1 pt-1.5 heading-label">Survey languages</DropdownMenuLabel>
                  <DropdownMenuItem
                    v-for="code in surveyLanguages"
                    :key="code"
                    class="text-13"
                    @select="emit('select', { action: 'translate', lang: code })">
                    {{ languageName(code) }}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator class="my-1.5" />
                </template>
                <DropdownMenuItem
                  v-for="code in otherLanguages"
                  :key="code"
                  class="text-13"
                  @select="emit('select', { action: 'translate', lang: code })">
                  {{ languageName(code) }}
                </DropdownMenuItem>
                <DropdownMenuSeparator class="my-1.5" />
                <div class="flex items-center gap-1.5 p-1">
                  <Input
                    v-model="customLang"
                    placeholder="e.g. Japanese"
                    class="h-8 text-13"
                    @keydown.stop
                    @keydown.enter.prevent="onTranslateCustom" />
                  <Button
                    type="button"
                    size="icon"
                    class="shrink-0"
                    :disabled="!customLang.trim()"
                    aria-label="Translate to this language"
                    @click="onTranslateCustom">
                    <ArrowRight />
                  </Button>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </div>

          <div class="border-t bg-page p-2.5">
            <Input
              v-model="customPromptValue"
              :icon="MessageSquare"
              placeholder="Custom request…"
              class="text-13"
              @keydown.stop
              @keydown.enter.exact.prevent="onCustom" />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  </Tooltip>
</template>
